/**
 * Swiper for blog-posts section. Mirrors collection-list-swiper.js behavior.
 */
(function () {
  const SWIPER_SELECTOR = "[data-blog-posts-swiper]";
  const HOST_SELECTOR = "[data-blog-posts-swiper-host]";

  function readNumber(value, fallback) {
    if (value === undefined || value === null || value === "") {
      return fallback;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }

  function readPositiveNumber(value, fallback) {
    const n = readNumber(value, NaN);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  }

  function parseOptions(el) {
    const d = el.dataset;
    return {
      speed: readPositiveNumber(d.bpSwiperSpeed, 400),
      loop: d.bpSwiperLoop === "true",
      centeredSlides: d.bpSwiperCentered === "true",
      autoplayDelay: Math.max(0, readNumber(d.bpSwiperAutoplayDelay, 0)),
      spaceBetweenMobile: Math.max(0, readNumber(d.bpSwiperSpaceMobile, 24)),
      spaceBetweenDesktop: Math.max(0, readNumber(d.bpSwiperSpaceDesktop, 24)),
      slidesPerViewMobile: readPositiveNumber(d.bpSwiperSpvMobile, 1.5),
      slidesPerViewDesktop: readPositiveNumber(d.bpSwiperSpvDesktop, 4),
      pagination: d.bpSwiperPagination === "true",
    };
  }

  function getSwiperConstructor() {
    const g = typeof globalThis !== "undefined" ? globalThis : window;
    return typeof g.Swiper === "function" ? g.Swiper : null;
  }

  function isRtl(el) {
    if (el?.getAttribute("dir") === "rtl") {
      return true;
    }
  }

  function progressScopeEl(swiperEl) {
    return (
      swiperEl.closest(".blog-post-list-swiper-scope") ||
      swiperEl.parentElement
    );
  }

  function updateProgressBar(swiper, scopeEl) {
    const bar = scopeEl?.querySelector(".blog-post-list-swiper__progress-bar");
    if (!bar) {
      return;
    }
    const total = swiper.slides?.length ?? 0;
    if (total <= 0) {
      return;
    }
    const raw = typeof swiper.progress === "number" ? swiper.progress : 0;
    const p = total === 1 ? 1 : raw * ((total - 1) / total) + 1 / total;
    bar.style.transform = `scaleX(${Math.min(1, Math.max(0, p))})`;
  }

  function createSwiper(el) {
    const SwiperCtor = getSwiperConstructor();
    if (!SwiperCtor || el.swiper) {
      return el.swiper ?? null;
    }

    const host = el.closest(HOST_SELECTOR);
    const prevBtn = host?.querySelector("[data-bp-swiper-prev] button");
    const nextBtn = host?.querySelector("[data-bp-swiper-next] button");
    const paginationEl = el.querySelector(
      ".blog-post-list-swiper__pagination",
    );

    const o = parseOptions(el);

    const autoplay =
      o.autoplayDelay > 0
        ? {
            delay: o.autoplayDelay,
            disableOnInteraction: true,
            pauseOnMouseEnter: true,
          }
        : false;

    const navigation =
      prevBtn && nextBtn
        ? {
            prevEl: prevBtn,
            nextEl: nextBtn,
          }
        : false;

    const pagination =
      o.pagination && paginationEl
        ? {
            el: paginationEl,
            clickable: true,
          }
        : false;

    const progressScope = progressScopeEl(el);

    try {
      return new SwiperCtor(el, {
        rtl: isRtl(el),
        speed: o.speed,
        loop: o.loop,
        centeredSlides: o.centeredSlides,
        slidesPerView: o.slidesPerViewMobile,
        spaceBetween: o.spaceBetweenMobile,
        watchOverflow: false,
        watchSlidesProgress: true,
        forceToAxis: true,
        simulateTouch: true,
        grabCursor: true,
        observer: false,
        observeParents: false,
        resizeObserver: true,
        navigation,
        pagination,
        autoplay,
        breakpoints: {
          700: {
            slidesPerView: o.slidesPerViewDesktop,
            spaceBetween: o.spaceBetweenDesktop,
          },
        },
        on: {
          init(s) {
            updateProgressBar(s, progressScope);
          },
          progress(s) {
            updateProgressBar(s, progressScope);
          },
          slideChange(s) {
            updateProgressBar(s, progressScope);
          },
        },
      });
    } catch (err) {
      console.warn("[blog-posts] Swiper init failed:", err);
      return null;
    }
  }

  function initSwiper(el) {
    function run() {
      if (!getSwiperConstructor()) {
        return false;
      }
      requestAnimationFrame(() => {
        const swiper = createSwiper(el);
        swiper?.update();
        swiper?.navigation?.update?.();
        if (swiper) {
          updateProgressBar(swiper, progressScopeEl(el));
        }
      });
      return true;
    }

    if (run()) {
      return;
    }

    let tries = 0;
    const id = window.setInterval(() => {
      tries += 1;
      if (run() || tries > 60) {
        window.clearInterval(id);
      }
    }, 50);
  }

  function boot(root = document) {
    root.querySelectorAll(SWIPER_SELECTOR).forEach((el) => {
      if (!el.swiper && el.closest(".shopify-section")) {
        initSwiper(el);
      }
    });
  }

  function destroyInRoot(root) {
    root.querySelectorAll(SWIPER_SELECTOR).forEach((el) => {
      if (el.swiper && typeof el.swiper.destroy === "function") {
        el.swiper.destroy(true, true);
      }
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot(document));
  } else {
    boot(document);
  }

  window.addEventListener("load", () => boot(document));

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target?.querySelector?.(SWIPER_SELECTOR)) {
      boot(event.target);
    }
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroyInRoot(event.target);
  });
})();
